import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { selectStateData } from 'src/core/redux/selectors';
import { addDetails } from 'src/actions';
import {
  ALIQUOTS_NUMBER,
  ALIQUOTS_VOLUME,
  NORMALIZATION_ALIQUOTS_NAMESPACE,
  NORMALIZATION_ALIQUOTS_NAMESPACE_ACTION,
  PLATE_SIZE_DETAILS_NAMESPACE,
  STEPS,
  CONCENTRATION,
  NORMALIZATION,
  PLATE_POOLING_NAMESPACE_ACTION,
  PLATE_POOLING_OPTIONS,
  NORMALIZATION_OPTIONS,
  PLATE_POOLING,
  ALIQUOTS_CHECKED,
  REMAINDER_CHECKED,
  REMAINDERS_NAMESPACE_ACTION,
} from 'src/constants/forms';
import { DnaOligo } from 'src/models';
import { toDecimalValue } from 'src/utils';

import NormalizationAliquotsComponent from './NormalizationAliquotsComponent';
import { aliquotsMaxValueValidator } from './components/Aliquots/validator';

const NormalizationAliquotsContainer = ({
  formData,
  addFormData,
  plateSizeDetails,
  addPoolingFormData,
  addRemaindersFormData,
}) => {
  const [showAliquotsCommonErr, setShowAliquotsCommonErr] = React.useState(false);
  const [maxValue, setMaxValue] = React.useState(0);
  const hasAliquots = formData?.[ALIQUOTS_CHECKED]?.value;
  const normalization = formData?.[NORMALIZATION]?.value;
  const onChangeData = (value, fieldName, isValid) => {
    if (fieldName === ALIQUOTS_NUMBER || fieldName === ALIQUOTS_VOLUME) {
      const valid = !aliquotsMaxValueValidator(
        formData,
        fieldName,
        value,
        setMaxValue,
        plateSizeDetails
      );

      setShowAliquotsCommonErr(!valid);

      addFormData({
        ...(formData || {}),
        [ALIQUOTS_NUMBER]: { value: fieldName === ALIQUOTS_NUMBER ?
          value : formData[ALIQUOTS_NUMBER]?.value,
        isValid: valid },
        [ALIQUOTS_VOLUME]: { value: fieldName === ALIQUOTS_VOLUME ?
          value : formData[ALIQUOTS_VOLUME]?.value,
        isValid: valid }
      });
    } else {
      addFormData({
        ...(formData || {}),
        [fieldName]: { value, isValid },
      });
    }
  };

  React.useEffect(() => {
    if (formData) {
      const valid = !aliquotsMaxValueValidator(
        formData,
        [ALIQUOTS_VOLUME],
        formData[ALIQUOTS_VOLUME]?.value,
        setMaxValue,
        plateSizeDetails
      );

      setShowAliquotsCommonErr(!valid);

      addFormData({
        ...(formData || {}),
        [CONCENTRATION]: { value: toDecimalValue(formData[CONCENTRATION]?.value), isValid: true },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!formData) {
      addFormData(DnaOligo.getDefaultFormState(STEPS.NORMALIZATION_ALIQUOTS));
    }
  }, [formData, addFormData]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    if (normalization && normalization !== NORMALIZATION_OPTIONS.CONCENTRATION_VOLUME) {
      // if any other option except volume concentration is selected make sure to auto select
      // none in plate pooling
      addPoolingFormData({
        [PLATE_POOLING]: { value: PLATE_POOLING_OPTIONS.NONE, isValid: true },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalization, addPoolingFormData]);

  React.useEffect(() => {
    if (
      !hasAliquots &&
      normalization && normalization !== NORMALIZATION_OPTIONS.CONCENTRATION_VOLUME
    ) {
      // auto disable remainders if aliquots is disabled and normalization is not
      // volume and concentration. This is so when disabling aliquots, from the normalization step,
      // remainders are disabled as well but when coming back from plate pooling with
      // remainders enabled it doesn't disable them unless the user checks a new normalization value
      addRemaindersFormData({
        [REMAINDER_CHECKED]: { value: false, isValid: true },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalization, hasAliquots, addRemaindersFormData]);

  return (
    <NormalizationAliquotsComponent
      {...{
        formData,
        onChangeData,
        plateSizeDetails,
        showAliquotsCommonErr,
        maxValue
      }}
    />
  );
};

NormalizationAliquotsContainer.propTypes = {
  formData: PropTypes.object,
  plateSizeDetails: PropTypes.object,
  addFormData: PropTypes.func.isRequired,
  addPoolingFormData: PropTypes.func.isRequired,
  addRemaindersFormData: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  formData: selectStateData(NORMALIZATION_ALIQUOTS_NAMESPACE)(state),
  plateSizeDetails: selectStateData(PLATE_SIZE_DETAILS_NAMESPACE)(state),
});

const mapDispatchToProps = {
  addFormData: addDetails(NORMALIZATION_ALIQUOTS_NAMESPACE_ACTION),
  addPoolingFormData: addDetails(PLATE_POOLING_NAMESPACE_ACTION),
  addRemaindersFormData: addDetails(REMAINDERS_NAMESPACE_ACTION),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NormalizationAliquotsContainer);
